import { Button, ConfirmationDialog, Icon, useOpen } from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { HotelStayFormValues, HotelStayStatus, WayOfBooking } from "../../../shared/types/hotel";

interface CancelRequestActionProps {
	onCancel: () => void;
	disabled: boolean;
}

const CancelRequestAction: FC<CancelRequestActionProps> = ({ onCancel, disabled }) => {
	const { t } = useTranslation();
	const { open, handleOpen, handleClose } = useOpen();

	return (
		<>
			<Button type="button" onClick={handleOpen} color="danger" prefix={<Icon $icon="delete" />} disabled={disabled}>
				{t("Request cancellation")}
			</Button>
			<ConfirmationDialog
				heading={t("Request cancellation")}
				labels={{ confirm: t("Confirm"), close: t("Close") }}
				open={open}
				onClose={handleClose}
				action={() => {
					onCancel();
					handleClose();
				}}
			>
				{t("Are you sure you want to request a cancellation of this hotel stay?")}
			</ConfirmationDialog>
		</>
	);
};

interface RevokeCancellationActionProps {
	onRevoke: () => void;
	disabled: boolean;
}

const RevokeCancellationAction: FC<RevokeCancellationActionProps> = ({ onRevoke, disabled }) => {
	const { t } = useTranslation();

	return (
		<Button type="button" onClick={onRevoke} disabled={disabled}>
			{t("Revoke cancellation")}
		</Button>
	);
};

interface DeleteHotelStayActionProps {
	onDelete: () => void;
	wayOfBooking: WayOfBooking;
	disabled: boolean;
}

const DeleteHotelStayAction: FC<DeleteHotelStayActionProps> = ({ onDelete, wayOfBooking, disabled }) => {
	const { t } = useTranslation();

	return (
		<Button type="button" onClick={onDelete} color="danger" prefix={<Icon $icon="delete" />} disabled={disabled}>
			{wayOfBooking === WayOfBooking.BOOKING ? t("Delete booking") : t("Delete inquiry")}
		</Button>
	);
};

interface HotelStayActionProps {
	wayOfBooking: WayOfBooking;
	hotelStay: HotelStayFormValues;
	onChange: (hotelStay: HotelStayFormValues) => void;
	onDelete: () => void;
	disabled: boolean;
}

export const HotelStayRemoveAction: FC<HotelStayActionProps> = ({
	wayOfBooking,
	hotelStay,
	onChange,
	onDelete,
	disabled,
}) => {
	const hasBooking =
		hotelStay.status === HotelStayStatus.BOOKING ||
		hotelStay.status === HotelStayStatus.CANCELLATION_REQUESTED ||
		hotelStay.status === HotelStayStatus.CONFLICT;

	if (hotelStay.cancellationRequested) {
		return (
			<RevokeCancellationAction
				onRevoke={() => onChange({ ...hotelStay, cancellationRequested: false })}
				disabled={disabled}
			/>
		);
	} else if (hasBooking) {
		return (
			<CancelRequestAction
				onCancel={() => onChange({ ...hotelStay, cancellationRequested: true })}
				disabled={disabled}
			/>
		);
	} else {
		return <DeleteHotelStayAction wayOfBooking={wayOfBooking} onDelete={onDelete} disabled={disabled} />;
	}
};
